import { useState, useMemo, useEffect, useCallback } from 'react';
import { DataGrid, KeenIcon } from '@/components';
import { ConfirmationDialog } from '@/components/dialogBox';
import { X } from 'lucide-react';
import usePagination from '@/hooks/usePagination';
import {
  useGetAllMembersQuery,
  useRemoveMemberMutation,
} from '@/services/ClubService';
import { useUpdateUserMutation } from '@/services/UserManagementService';
import { useParams } from 'react-router-dom';
import {
  MemberResponse,
  IMemberData,
  PaginatedApiResponse,
  MemberQueryParams,
} from '@/types/club-management.types';
import { usePermissions } from '@/hooks/usePermissions';
import { enqueueSnackbar } from 'notistack';
import { formatDisplayDate } from '@/utils/Date';

const MembersListing = () => {
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const [confirmRemoveState, setConfirmRemoveState] = useState<{
    isOpen: boolean;
    memberId: string | null;
  }>({ isOpen: false, memberId: null });
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const [dropdownCoords, setDropdownCoords] = useState<
    Record<string, { top: number; left: number }>
  >({});
  const [dropdownAnchors, setDropdownAnchors] = useState<
    Record<
      string,
      {
        buttonTop: number;
        buttonBottom: number;
        buttonRight: number;
        openDirection: 'up' | 'down';
      }
    >
  >({});
  const [roleChangeState, setRoleChangeState] = useState<{
    isOpen: boolean;
    memberId: string | null;
    newRole: string | null;
  }>({ isOpen: false, memberId: null, newRole: null });
  const [roleModalState, setRoleModalState] = useState<{
    isOpen: boolean;
    memberId: string | null;
    memberName: string | null;
  }>({ isOpen: false, memberId: null, memberName: null });
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [revokeRoleState, setRevokeRoleState] = useState<{
    isOpen: boolean;
    memberId: string | null;
  }>({ isOpen: false, memberId: null });
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = usePermissions();

  // Map status filter to API status value
  const getStatusEnum = (filter: string): number | undefined => {
    const statusMap: { [key: string]: number } = {
      active: 1, // Assuming 1 is Active (Approved)
      suspended: 2, // Adjust based on API documentation
    };
    return filter !== 'all' ? statusMap[filter] : undefined;
  };

  // Fetch members from API with club_id and status parameters
  const queryParams: MemberQueryParams = {
    club_id: id || undefined,
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue || undefined,
    status: getStatusEnum(statusFilter),
  };

  const {
    data,
    isLoading: isMembersLoading,
    isFetching: isMembersFetching,
    isError: isMembersError,
    refetch: refetchMembers,
  } = useGetAllMembersQuery(queryParams) as {
    data?: PaginatedApiResponse<MemberResponse>;
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
    refetch: () => void;
  };

  // Mutation for removing a member
  const [removeMember, { isLoading: isRemoving }] = useRemoveMemberMutation();

  // Mutation for updating user role
  const [updateUser, { isLoading: isUpdatingRole }] = useUpdateUserMutation();

  // Update pagination totals
  useEffect(() => {
    if (data) {
      handleTotalPages(
        data.totalPages || Math.ceil(data.total / pagination.pageSize) || 1
      );
      handleTotalItems((data.totalItems ?? data.total) || 0);
    }
  }, [data, handleTotalPages, handleTotalItems, pagination.pageSize]);

  // Refetch on filter changes
  useEffect(() => {
    refetchMembers();
  }, [id, statusFilter, debouncedValue, refetchMembers]);

  // Ensure body scroll is restored if dropdown is closed/unmounted
  useEffect(() => {
    document.body.style.overflow = dropdownOpen ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [dropdownOpen]);

  // When opening upwards, measure the actual dropdown height and reposition.
  useEffect(() => {
    if (!dropdownOpen) return;

    const anchor = dropdownAnchors[dropdownOpen];
    if (!anchor || anchor.openDirection !== 'up') return;

    const raf = window.requestAnimationFrame(() => {
      const el = document.querySelector(
        `[data-dropdown-id="${dropdownOpen}"]`
      ) as HTMLElement | null;
      if (!el) return;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const dropdownRect = el.getBoundingClientRect();
      const dropdownWidth = dropdownRect.width || 224;
      const dropdownHeight = dropdownRect.height || 200;
      const gap = 8;

      const desiredTop = Math.max(
        gap,
        Math.min(
          viewportHeight - dropdownHeight - gap,
          anchor.buttonTop - dropdownHeight - gap
        )
      );
      const desiredLeft = Math.min(
        viewportWidth - dropdownWidth - gap,
        Math.max(gap, anchor.buttonRight - dropdownWidth)
      );

      setDropdownCoords((prev) => ({
        ...prev,
        [dropdownOpen]: { top: desiredTop, left: desiredLeft },
      }));
    });

    return () => {
      window.cancelAnimationFrame(raf);
    };
  }, [dropdownAnchors, dropdownOpen]);

  const transformData = (members: MemberResponse[]): IMemberData[] => {
    return members.map((member) => ({
      id: member?.id,
      name: member.user?.name || 'Unknown',
      email: member.user?.email || 'No email',
      phone: member.user?.mobile || 'No phone',
      join_date: member.created_at,
      status: member.member_status === 1 ? 'Active' : 'Suspended',
      club_id: member.club_id,
      roles:
        member.user?.roles && member.user?.roles.length > 0
          ? member.user?.roles
          : ['member'], // Get all roles or default to member
      user_id: member.user?.id,
    }));
  };

  const filteredData = useMemo(() => {
    if (!data?.data) return [];
    return transformData(data?.data);
  }, [data]);

  const resetFilters = () => {
    if (searchTerm || statusFilter !== 'all') {
      resetSearch();
      setStatusFilter('all');
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  };

  const handleRemoveClick = useCallback((memberId: string) => {
    setConfirmRemoveState({ isOpen: true, memberId });
  }, []);

  const handleConfirmRemove = async () => {
    if (confirmRemoveState.memberId) {
      try {
        await removeMember(confirmRemoveState.memberId).unwrap();
        refetchMembers();
        setConfirmRemoveState({ isOpen: false, memberId: null });
        enqueueSnackbar('Member removed successfully', { variant: 'success' });
      } catch (error) {
        console.error('Failed to remove member:', error);
        enqueueSnackbar('Failed to remove member', { variant: 'error' });
      }
    }
  };

  const handleChangeRoleClick = useCallback(
    (memberId: string) => {
      const member = filteredData.find((m) => m.id === memberId);
      setRoleModalState({
        isOpen: true,
        memberId,
        memberName: member?.name || 'Unknown',
      });
      setSelectedRole(null);
      setDropdownOpen(null);
    },
    [filteredData]
  );

  const handleRoleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRole(e.target.value);
  };

  const handleSaveRole = async () => {
    if (!selectedRole || !roleModalState.memberId) return;

    setRoleChangeState({
      isOpen: true,
      memberId: roleModalState.memberId,
      newRole: selectedRole,
    });
    setRoleModalState({ isOpen: false, memberId: null, memberName: null });
  };

  const getRoleDisplayName = (role: string): string => {
    const roleDisplayMap: { [key: string]: string } = {
      inspector: 'Inspector - Can perform inspections',
      chief_inspector:
        'Chief Inspector - Can manage inspections and inspectors',
      member: 'Member - Basic club member',
      boater: 'Boater - Registered boat owner',
    };
    return roleDisplayMap[role] || role;
  };

  const handleRevokeRoleClick = useCallback((memberId: string) => {
    setRevokeRoleState({ isOpen: true, memberId });
    setDropdownOpen(null);
  }, []);

  const handleConfirmRoleChange = async () => {
    if (roleChangeState.memberId && roleChangeState.newRole) {
      try {
        const member = filteredData.find(
          (m) => m.id === roleChangeState.memberId
        );
        if (!member?.user_id) {
          throw new Error('User ID not found');
        }

        const currentRoles =
          data?.data.find((m) => m.id === roleChangeState.memberId)?.user
            .roles || [];
        let updatedRoles = [...currentRoles];

        if (roleChangeState.newRole === 'inspector') {
          updatedRoles = updatedRoles.filter(
            (role) => role !== 'chief_inspector'
          );
          if (!updatedRoles.includes('inspector')) {
            updatedRoles.push('inspector');
          }
        } else if (roleChangeState.newRole === 'chief_inspector') {
          updatedRoles = updatedRoles.filter((role) => role !== 'inspector');
          if (!updatedRoles.includes('chief_inspector')) {
            updatedRoles.push('chief_inspector');
          }
        }

        const formData = new FormData();
        updatedRoles.forEach((role) => {
          formData.append('roles[]', role);
        });

        await updateUser({
          user_id: member.user_id,
          data: formData,
        }).unwrap();

        refetchMembers();
        setRoleChangeState({ isOpen: false, memberId: null, newRole: null });
        enqueueSnackbar(
          `Role updated to ${roleChangeState.newRole.replace('_', ' ').toUpperCase()} successfully`,
          { variant: 'success' }
        );
      } catch (error) {
        console.error('Failed to update role:', error);
        enqueueSnackbar('Failed to update role', { variant: 'error' });
      }
    }
  };

  const handleConfirmRevokeRole = async () => {
    if (revokeRoleState.memberId) {
      try {
        const member = filteredData.find(
          (m) => m.id === revokeRoleState.memberId
        );
        if (!member?.user_id) {
          throw new Error('User ID not found');
        }

        const currentRoles =
          data?.data.find((m) => m.id === revokeRoleState.memberId)?.user
            .roles || [];
        const updatedRoles = currentRoles.filter(
          (role) => role !== 'inspector' && role !== 'chief_inspector'
        );

        const formData = new FormData();
        updatedRoles.forEach((role) => {
          formData.append('roles[]', role);
        });

        await updateUser({
          user_id: member.user_id,
          data: formData,
        }).unwrap();

        refetchMembers();
        setRevokeRoleState({ isOpen: false, memberId: null });
        enqueueSnackbar(
          'Inspector/Chief Inspector roles revoked successfully',
          { variant: 'success' }
        );
      } catch (error) {
        console.error('Failed to revoke roles:', error);
        enqueueSnackbar('Failed to revoke roles', { variant: 'error' });
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        accessor: (row: IMemberData) => row.email,
        id: 'email',
        header: 'Email',
        enableSorting: false,
        cell: ({ row }: { row: { original: IMemberData } }) => (
          <span className="text-gray-800 font-normal break-all md:break-normal">
            {row.original.email}
          </span>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: IMemberData) => row.phone,
        id: 'phone',
        header: 'Phone',
        enableSorting: false,
        cell: ({ row }: { row: { original: IMemberData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.phone}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: IMemberData) => row.join_date,
        id: 'join_date',
        header: 'Join Date',
        enableSorting: false,
        cell: ({ row }: { row: { original: IMemberData } }) => (
          <span className="text-gray-800 font-normal">
            {formatDisplayDate(new Date(row.original.join_date))}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: IMemberData) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: IMemberData } }) => (
          <span
            className={`text-sm font-medium ${
              row.original.status === 'Active'
                ? 'text-green-600'
                : 'text-red-600'
            }`}
          >
            {row.original.status}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: IMemberData) => row.roles,
        id: 'roles',
        header: 'Role',
        enableSorting: false,
        cell: ({ row }: { row: { original: IMemberData } }) => {
          const roles = row.original.roles || ['member'];

          return (
            <div className="flex flex-wrap gap-2">
              {roles.map((role, index) => (
                <span
                  key={`${role}-${index}`}
                  className="text-gray-800 font-normal text-sm"
                  aria-label={`Member role: ${role}`}
                >
                  {role.replace('_', ' ').toUpperCase()}
                  {index < roles.length - 1 && (
                    <span className="text-gray-400 contrast:text-gray-500 ml-1">
                      •
                    </span>
                  )}
                </span>
              ))}
            </div>
          );
        },
        meta: { className: 'min-w-[150px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: IMemberData } }) => (
          <div className="relative">
            <button
              type="button"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();

                const isOpening = dropdownOpen !== row.original.id;
                setDropdownOpen(isOpening ? row.original.id : null);

                if (isOpening) {
                  const buttonRect = e.currentTarget.getBoundingClientRect();
                  const viewportHeight = window.innerHeight;
                  const viewportWidth = window.innerWidth;
                  const spaceBelow = viewportHeight - buttonRect.bottom;
                  const estimatedDropdownHeight = 200;
                  const dropdownWidth = 224; // w-56

                  const tableRows = filteredData.length;
                  const currentRowIndex = filteredData.findIndex(
                    (item) => item.id === row.original.id
                  );
                  const shouldShowUp =
                    spaceBelow < estimatedDropdownHeight ||
                    currentRowIndex >= tableRows - 2;

                  setDropdownAnchors((prev) => ({
                    ...prev,
                    [row.original.id]: {
                      buttonTop: buttonRect.top,
                      buttonBottom: buttonRect.bottom,
                      buttonRight: buttonRect.right,
                      openDirection: shouldShowUp ? 'up' : 'down',
                    },
                  }));

                  const desiredTop = shouldShowUp
                    ? Math.max(8, buttonRect.top - estimatedDropdownHeight - 8)
                    : buttonRect.bottom + 8;
                  const desiredLeft = Math.min(
                    viewportWidth - dropdownWidth - 8,
                    Math.max(8, buttonRect.right - dropdownWidth)
                  );

                  setDropdownCoords((prev) => ({
                    ...prev,
                    [row.original.id]: { top: desiredTop, left: desiredLeft },
                  }));
                }
              }}
              className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Actions"
              aria-label={`Actions for ${row.original.name}`}
              aria-haspopup="true"
              aria-expanded={dropdownOpen === row.original.id}
              disabled={
                isMembersLoading ||
                isMembersFetching ||
                isRemoving ||
                isUpdatingRole
              }
            >
              <KeenIcon
                type="filled"
                icon="dots-vertical"
                className="text-gray-500"
                aria-hidden="true"
              />
            </button>
            {dropdownOpen === row.original.id && (
              <div
                className="fixed inset-0 z-[9999]"
                onPointerDown={() => setDropdownOpen(null)}
                role="presentation"
              >
                <div
                  className="fixed z-[10000] w-56 rounded-md bg-gray-100 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dropdown-container"
                  style={dropdownCoords[row.original.id]}
                  onPointerDown={(e) => e.stopPropagation()}
                  role="menu"
                  aria-label="Member actions"
                  data-dropdown-id={row.original.id}
                >
                  <div className="py-1">
                    {/* Change Roles Option */}
                    <button
                      type="button"
                      onClick={() => handleChangeRoleClick(row.original.id)}
                      className="block w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-gray-100"
                      role="menuitem"
                      aria-label={`Change role for ${row.original.name}`}
                      disabled={
                        isUpdatingRole ||
                        isMembersLoading ||
                        isMembersFetching ||
                        isRemoving
                      }
                    >
                      Change Roles
                    </button>

                    {/* Revoke Roles Option */}
                    <button
                      type="button"
                      onClick={() => handleRevokeRoleClick(row.original.id)}
                      className="block w-full text-left px-4 py-2 text-sm text-orange-600 hover:bg-gray-100"
                      role="menuitem"
                      aria-label={`Revoke roles for ${row.original.name}`}
                      disabled={
                        isUpdatingRole ||
                        isMembersLoading ||
                        isMembersFetching ||
                        isRemoving
                      }
                    >
                      Revoke Roles
                    </button>

                    {/* Divider */}
                    <div className="border-t border-gray-100 my-1"></div>

                    {/* Remove Action */}
                    {hasPermission('remove_club_member') && (
                      <button
                        type="button"
                        onClick={() => {
                          handleRemoveClick(row.original.id);
                          setDropdownOpen(null);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                        role="menuitem"
                        aria-label={`Remove ${row.original.name}`}
                        disabled={
                          isUpdatingRole ||
                          isMembersLoading ||
                          isMembersFetching ||
                          isRemoving
                        }
                      >
                        Remove Member
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ),
        meta: { className: 'w-[120px]' },
      },
    ],
    [
      isMembersLoading,
      isMembersFetching,
      isRemoving,
      isUpdatingRole,
      hasPermission,
      dropdownOpen,
      dropdownCoords,
      filteredData,
      handleRemoveClick,
      handleChangeRoleClick,
      handleRevokeRoleClick,
      setDropdownOpen,
      setDropdownCoords,
    ]
  );

  return (
    <div className="card card-grid min-w-full">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <div className="flex-shrink-0">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-members"
              aria-label="Search members"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
              />
              <input
                id="search-members"
                placeholder="Search members"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search members by name or email"
                disabled={isMembersLoading || isMembersFetching || isRemoving}
              />
            </label>
          </div>
          <div className="flex-shrink-0">
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              className="select select-sm h-8 sm:h-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-[30px]"
              aria-label="Filter members by status"
              disabled={isMembersLoading || isMembersFetching || isRemoving}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(searchTerm || statusFilter !== 'all') && (
            <button
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={resetFilters}
              aria-label="Clear all filters"
              title="Clear all filters"
              disabled={isMembersLoading || isMembersFetching || isRemoving}
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
              />
              Clear Filters
            </button>
          )}
        </div>
      </div>
      <div className="card-body">
        {isMembersLoading || isMembersFetching || isRemoving ? (
          <div className="text-center py-4" role="status" aria-live="polite">
            <KeenIcon
              type="filled"
              icon="spinner"
              className="text-gray-500 text-2xl animate-spin"
            />
            <span className="ml-2 text-gray-700">Loading...</span>
          </div>
        ) : isMembersError ? (
          <div className="text-center py-4 text-red-600" role="alert">
            Error loading data. Please try again.
          </div>
        ) : (
          <DataGrid
            columns={columns}
            data={filteredData}
            rowSelect={false}
            loading={isMembersLoading || isMembersFetching || isRemoving}
            pagination={{
              page: pagination.pageIndex,
              size: pagination.pageSize,
              totalItems: data?.total || totalItems,
              totalPages: data?.totalPages || totalPages,
              setPagination,
              info: `{from} - {to} of {count}`,
              sizes: [5, 10, 25, 50, 100],
              sizesLabel: 'Show',
              sizesDescription: 'per page',
            }}
            sorting={[{ id: 'name', desc: false }]}
            serverSide={true}
            onFilterReset={resetFilters}
            hasActiveFilters={!!searchTerm || statusFilter !== 'all'}
            aria-label="Club members table"
          />
        )}
        <ConfirmationDialog
          open={confirmRemoveState.isOpen}
          onClose={() =>
            setConfirmRemoveState({ isOpen: false, memberId: null })
          }
          onConfirm={handleConfirmRemove}
          message="Are you sure you want to remove this member?"
          cancelButtonText="No"
          confirmButtonText="Yes"
          aria-labelledby="remove-confirmation-title"
          aria-describedby="remove-confirmation-description"
        />
        <ConfirmationDialog
          open={roleChangeState.isOpen}
          onClose={() =>
            setRoleChangeState({ isOpen: false, memberId: null, newRole: null })
          }
          onConfirm={handleConfirmRoleChange}
          message={`Are you sure you want to assign the role "${roleChangeState.newRole?.replace('_', ' ').toUpperCase()}" to this member?`}
          cancelButtonText="Cancel"
          confirmButtonText="Assign Role"
          aria-labelledby="role-change-confirmation-title"
          aria-describedby="role-change-confirmation-description"
        />

        {/* Role Selection Modal */}
        {roleModalState.isOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999] p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="member-role-modal-title"
          >
            <div
              className="bg-gray-100 rounded-xl shadow-xl max-w-md w-full overflow-y-auto custom-scrollbar"
              role="document"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h1
                    id="member-role-modal-title"
                    className="text-lg font-semibold text-gray-900"
                  >
                    Update Member Role
                  </h1>
                  <button
                    type="button"
                    onClick={() =>
                      setRoleModalState({
                        isOpen: false,
                        memberId: null,
                        memberName: null,
                      })
                    }
                    className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label="Close modal"
                    title="Close"
                  >
                    <X className="w-5 h-5" aria-hidden="true" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="block text-sm font-medium text-gray-700 mb-1">
                      Member
                    </p>
                    <div
                      className="p-3 bg-gray-200 rounded-lg"
                      role="region"
                      aria-label="Member information"
                    >
                      <p className="font-medium">
                        {roleModalState.memberName || 'Unknown Member'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {filteredData.find(
                          (m) => m.id === roleModalState.memberId
                        )?.email || 'No email'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="block text-sm font-medium text-gray-700 mb-1">
                      Current Role
                    </p>
                    <div
                      className="p-3 bg-blue-50 dark:bg-blue-950 contrast:bg-blue-950 text-blue-700 dark:text-blue-200 contrast:text-blue-200 rounded-lg"
                      role="status"
                      aria-label="Current member role"
                    >
                      {getRoleDisplayName(
                        filteredData.find(
                          (m) => m.id === roleModalState.memberId
                        )?.roles?.[0] || 'member'
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="role-select"
                      className="block text-sm font-medium text-gray-700"
                    >
                      New Role
                      <span className="text-red-500 ml-1" aria-label="required">
                        *
                      </span>
                    </label>
                    <div className="relative">
                      <select
                        id="role-select"
                        className="block w-full pl-3 pr-10 py-2.5 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md bg-light-active appearance-none"
                        value={selectedRole || ''}
                        onChange={handleRoleSelectChange}
                        onFocus={() => setIsSelectOpen(true)}
                        onBlur={() => setIsSelectOpen(false)}
                        disabled={isUpdatingRole}
                        required
                        aria-required="true"
                        aria-label="Select new role"
                      >
                        <option value="">Select a role...</option>
                        <option value="inspector">
                          {getRoleDisplayName('inspector')}
                        </option>
                        <option value="chief_inspector">
                          {getRoleDisplayName('chief_inspector')}
                        </option>
                      </select>
                      <div
                        className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700"
                        aria-hidden="true"
                      >
                        {isSelectOpen ? (
                          <svg
                            className="h-4 w-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            aria-hidden="true"
                          >
                            <path
                              fillRule="evenodd"
                              d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="h-4 w-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            aria-hidden="true"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>

                  <div
                    className="bg-yellow-50 dark:bg-yellow-900 contrast:bg-yellow-900 p-3 rounded-lg"
                    role="note"
                    aria-label="Important information"
                  >
                    <p className="text-sm text-yellow-700 dark:text-yellow-200 contrast:text-yellow-200">
                      <span className="font-medium">Note:</span> Changing a
                      member's role will modify their permissions in this club.
                      Inspector and Chief Inspector roles are mutually
                      exclusive.
                    </p>
                  </div>
                </div>

                <div
                  className="flex gap-3 mt-6"
                  role="group"
                  aria-label="Form actions"
                >
                  <button
                    type="button"
                    onClick={() =>
                      setRoleModalState({
                        isOpen: false,
                        memberId: null,
                        memberName: null,
                      })
                    }
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    disabled={isUpdatingRole}
                    aria-label="Cancel and close modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    onClick={handleSaveRole}
                    disabled={isUpdatingRole || !selectedRole}
                    className={`flex-1 px-4 py-2 rounded-lg text-white ${
                      isUpdatingRole || !selectedRole
                        ? 'bg-blue-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                    aria-label={
                      isUpdatingRole ? 'Saving role' : 'Save role changes'
                    }
                    aria-busy={isUpdatingRole}
                  >
                    {isUpdatingRole ? 'Saving...' : 'Save Role'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Revoke Role Confirmation */}
        <ConfirmationDialog
          open={revokeRoleState.isOpen}
          onClose={() => setRevokeRoleState({ isOpen: false, memberId: null })}
          onConfirm={handleConfirmRevokeRole}
          message="Are you sure you want to revoke all inspector and chief inspector roles from this member? They will keep their other roles like boater."
          cancelButtonText="Cancel"
          confirmButtonText="Revoke Roles"
          aria-labelledby="revoke-role-confirmation-title"
          aria-describedby="revoke-role-confirmation-description"
        />
      </div>
    </div>
  );
};

export { MembersListing };
